import { useState, useEffect, useCallback } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../store/authStore';
import {
  Copy, RefreshCw, CheckCircle2, Clock, AlertTriangle,
  Link2, Hash, RotateCcw, UserPlus, Search, Filter,
  MessageCircle, QrCode, X, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { StyledQrCode } from '../../security/qr/StyledQrCode';

const API = import.meta.env.VITE_API_URL || '/api/v1';

interface MembroAtivacao {
  id: string;
  nome: string;
  email: string;
  telefone?: string | null;
  cargo: string | null;
  perfil: string;
  statusConta: 'PENDENTE' | 'ATIVANDO' | 'ATIVA';
  tokenAtivacao: string | null;
  codigoDisplay: string | null;
  tokenExpiraEm: string | null;
  criadoEm: string;
}

const CARGO_LABEL: Record<string, string> = {
  DIRETOR: 'Diretor', COORDENACAO: 'Coordenação', SECRETARIA: 'Secretaria',
  SEGURANCA: 'Segurança', PROFESSOR: 'Professor', AUXILIAR: 'Auxiliar', OUTRO: 'Outro',
};

const STATUS_CONFIG = {
  PENDENTE:  { label: 'Aguardando',  cor: '#f59e0b', Icone: Clock },
  ATIVANDO:  { label: 'Ativando...',  cor: '#3b82f6', Icone: RefreshCw },
  ATIVA:     { label: 'Ativa',       cor: '#10b981', Icone: CheckCircle2 },
};

function badgeStyle(status: string) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.PENDENTE;
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem',
    fontWeight: 700, padding: '3px 10px', borderRadius: 20,
    background: `${cfg.cor}22`, color: cfg.cor, border: `1px solid ${cfg.cor}44`,
  };
}

// â”€â”€ Modal QR Code â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function QRModal({
  membros, indiceInicial, onClose,
}: { membros: MembroAtivacao[]; indiceInicial: number; onClose: () => void }) {
  const [idx, setIdx] = useState(indiceInicial);
  const m = membros[idx];
  const baseURL = `${window.location.protocol}//${window.location.host}`;
  const link = m?.tokenAtivacao ? `${baseURL}/ativar?token=${m.tokenAtivacao}${m.codigoDisplay ? `&code=${m.codigoDisplay}` : ''}` : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9990,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 20, padding: '2rem',
        maxWidth: 420, width: '100%', textAlign: 'center',
        border: '1px solid var(--border-color)', position: 'relative',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
          <X size={18} />
        </button>

        <p style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          QR Code — {CARGO_LABEL[m?.cargo ?? ''] ?? m?.cargo ?? m?.perfil}
        </p>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-main)', margin: '0 0 1.5rem' }}>{m?.nome}</h3>

        {link ? (
          <div style={{ display: 'inline-block', padding: 12, borderRadius: 16, background: '#fff', marginBottom: '1rem' }}>
            <StyledQrCode
              value={link}
              size={220}
              width={220}
              height={220}
              shape="square"
              style="rounded"
              icon="logo-shield-qrcode-backing"
              iconKind="preset"
              iconSize={100}
              hideBackgroundDots={false}
              foreground="#7c3aed"
              background="#ffffff"
              margin={0}
            />
          </div>
        ) : (
          <div style={{ padding: '3rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Link de ativação não disponível</div>
        )}

        {m?.codigoDisplay && (
          <p style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '0.2em', color: 'var(--text-main)', fontFamily: 'monospace', margin: '0 0 1.5rem' }}>
            Código: {m.codigoDisplay}
          </p>
        )}

        {m?.tokenExpiraEm && (
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 1.5rem' }}>
            Expira em: {new Date(m.tokenExpiraEm).toLocaleDateString('pt-BR')}
          </p>
        )}

        {/* Navegação entre membros */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button disabled={idx === 0} onClick={() => setIdx(i => i - 1)} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent',
            color: 'var(--text-muted)', cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, opacity: idx === 0 ? 0.4 : 1,
          }}>
            <ChevronLeft size={14} /> Anterior
          </button>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', alignSelf: 'center' }}>{idx + 1} / {membros.length}</span>
          <button disabled={idx === membros.length - 1} onClick={() => setIdx(i => i + 1)} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent',
            color: 'var(--text-muted)', cursor: idx === membros.length - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, opacity: idx === membros.length - 1 ? 0.4 : 1,
          }}>
            Próximo <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// â”€â”€ Componente Principal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function AtivacoesPage() {
  const { canManage, canView } = usePermissions();

  const { user } = useAuthStore();
  const escolaId = user?.escolaId ?? '';
  const token = (localStorage.getItem('ac_token') || '');
  const nomeEscola = (user as any)?.escola?.nome ?? 'Aluno Conectado';

  const [membros, setMembros] = useState<MembroAtivacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroCargo, setFiltroCargo] = useState('TODOS');
  const [copiadoId, setCopiadoId] = useState<string | null>(null);
  const [reenviandoId, setReenviandoId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [qrModal, setQrModal] = useState<{ membros: MembroAtivacao[]; idx: number } | null>(null);

  const baseURL = `${window.location.protocol}//${window.location.host}`;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/escolas/${escolaId}/equipe`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const equipe: MembroAtivacao[] = await res.json();
      const resP = await fetch(`${API}/escolas/${escolaId}/equipe?cargo=PROFESSOR`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const professores: MembroAtivacao[] = resP.ok ? await resP.json() : [];
      const todos = [...equipe, ...professores];
      const uniq = todos.filter((v, i, a) => a.findIndex(x => x.id === v.id) === i);
      setMembros(uniq);
    } catch {
      showToast('Erro ao carregar membros.');
    } finally {
      setLoading(false);
    }
  }, [escolaId, token]);

  useEffect(() => { if (escolaId) carregar(); }, [carregar, escolaId]);

  const copiar = async (texto: string, id: string) => {
    await navigator.clipboard.writeText(texto);
    setCopiadoId(id);
    setTimeout(() => setCopiadoId(null), 2000);
    showToast('Copiado!');
  };

  const reenviar = async (userId: string) => {
    setReenviandoId(userId);
    try {
      const res = await fetch(`${API}/auth/resend-invite/${userId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      showToast('Novo link gerado com sucesso!');
      await carregar();
    } catch {
      showToast('Erro ao gerar novo link.');
    } finally {
      setReenviandoId(null);
    }
  };

  /** Doc 15 — Canal 1: WhatsApp com mensagem pré-preenchida */
  const enviarWhatsApp = (m: MembroAtivacao) => {
    const link = m.tokenAtivacao ? `${baseURL}/ativar?token=${m.tokenAtivacao}${m.codigoDisplay ? `&code=${m.codigoDisplay}` : ''}` : null;
    const expiracao = m.tokenExpiraEm ? new Date(m.tokenExpiraEm).toLocaleDateString('pt-BR') : '30 dias';
    const cargo = CARGO_LABEL[m.cargo ?? ''] ?? m.cargo ?? 'Membro da equipe';

    const texto = [
      `Olá! Você foi cadastrado(a) como ${cargo} na ${nomeEscola} — Aluno Conectado.`,
      '',
      link ? `Para ativar sua conta, acesse:\nðŸ”— ${link}` : `Seu código de ativação: ${m.codigoDisplay}`,
      '',
      `Expira em: ${expiracao}.`,
      'Qualquer dúvida, fale com a direção da escola.',
    ].join('\n');

    // Se tem telefone, vai direto; senão abre WhatsApp Web genérico
    const telefone = (m as any).telefone?.replace(/\D/g, '') ?? '';
    const waURL = telefone
      ? `https://wa.me/55${telefone}?text=${encodeURIComponent(texto)}`
      : `https://web.whatsapp.com/?text=${encodeURIComponent(texto)}`;

    window.open(waURL, '_blank');
  };

  /** Doc 15 — Canal 3: Abrir modal de QR Code */
  const abrirQR = (membrosQR: MembroAtivacao[], idx: number) => {
    setQrModal({ membros: membrosQR, idx });
  };

  const filtrados = membros.filter(m => {
    const matchBusca = m.nome.toLowerCase().includes(busca.toLowerCase()) ||
      m.email.toLowerCase().includes(busca.toLowerCase());
    const matchCargo = filtroCargo === 'TODOS' || m.cargo === filtroCargo || m.perfil === filtroCargo;
    return matchBusca && matchCargo;
  });

  const pendentes = membros.filter(m => m.statusConta !== 'ATIVA');
  const comLink = pendentes.filter(m => m.tokenAtivacao && new Date(m.tokenExpiraEm ?? '2000') > new Date());

  return (
    <div style={{ padding: '2rem 2rem 5rem', maxWidth: 920, margin: '0 auto' }}>

      {/* QR Modal */}
      {qrModal && (
        <QRModal
          membros={qrModal.membros}
          indiceInicial={qrModal.idx}
          onClose={() => setQrModal(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, background: '#1e293b', color: '#f8fafc',
          padding: '12px 20px', borderRadius: 12, fontSize: '0.85rem', fontWeight: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 9999, border: '1px solid rgba(255,255,255,0.1)',
        }}>{toast}</div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UserPlus size={20} style={{ color: '#fff' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: 'var(--text-main)' }}>
              Painel de Ativações
            </h1>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
              Gerencie os convites da equipe e professores
            </p>
          </div>
        </div>

        {/* Ações em massa */}
        {comLink.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              onClick={() => abrirQR(comLink, 0)}
              style={{
                padding: '8px 14px', borderRadius: 9, border: '1px solid #6c5ce744',
                background: '#6c5ce711', color: '#6c5ce7', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600,
              }}
            >
              <QrCode size={14} /> QR Codes — Todos os Pendentes ({comLink.length})
            </button>
          </div>
        )}

        {pendentes.length > 0 && (
          <div style={{
            background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
            padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
          }}>
            <AlertTriangle size={16} style={{ color: '#d97706' }} />
            <span style={{ fontSize: '0.85rem', color: '#92400e', fontWeight: 600 }}>
              {pendentes.length} membro{pendentes.length > 1 ? 's' : ''} ainda não ativou a conta.
            </span>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por nome ou email..."
            style={{
              width: '100%', padding: '9px 12px 9px 36px', borderRadius: 10,
              border: '1.5px solid var(--border-color)', background: 'var(--bg-card)',
              color: 'var(--text-main)', fontSize: '0.85rem', boxSizing: 'border-box',
            }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={14} style={{ color: 'var(--text-muted)' }} />
          <select value={filtroCargo} onChange={e => setFiltroCargo(e.target.value)}
            style={{
              padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--border-color)',
              background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.85rem',
            }}>
            <option value="TODOS">Todos os cargos</option>
            <option value="COORDENACAO">Coordenação</option>
            <option value="SECRETARIA">Secretaria</option>
            <option value="SEGURANCA">Segurança</option>
            <option value="PROFESSOR">Professores</option>
          </select>
        </div>
        <button onClick={carregar} style={{
          padding: '9px 16px', borderRadius: 10, border: '1.5px solid var(--border-color)',
          background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem',
        }}>
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <p>Carregando membros...</p>
        </div>
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <CheckCircle2 size={40} style={{ color: '#10b981', marginBottom: 12 }} />
          <p style={{ fontWeight: 600 }}>Nenhum membro encontrado.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtrados.map((m, idx) => {
            const cfg = STATUS_CONFIG[m.statusConta] ?? STATUS_CONFIG.PENDENTE;
            const StatusIcn = cfg.Icone;
            const link = m.tokenAtivacao ? `${baseURL}/ativar?token=${m.tokenAtivacao}${m.codigoDisplay ? `&code=${m.codigoDisplay}` : ''}` : null;
            const expirou = m.tokenExpiraEm ? new Date(m.tokenExpiraEm) < new Date() : false;
            const isCopied = copiadoId === m.id;
            const isReenviando = reenviandoId === m.id;

            return (
              <div key={m.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border-color)',
                borderRadius: 14, padding: '1.2rem 1.5rem',
                borderLeft: `3px solid ${cfg.cor}`,
                opacity: m.statusConta === 'ATIVA' ? 0.7 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-main)' }}>{m.nome}</span>
                      <span style={badgeStyle(m.statusConta)}>
                        <StatusIcn size={10} />{cfg.label}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 4px' }}>{m.email}</p>
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0 }}>
                      {CARGO_LABEL[m.cargo ?? ''] ?? m.cargo ?? m.perfil}
                      {m.tokenExpiraEm && m.statusConta !== 'ATIVA' && (
                        <span style={{ marginLeft: 8, color: expirou ? '#ef4444' : '#94a3b8' }}>
                          {expirou ? 'âš  Link expirado' : `Expira ${new Date(m.tokenExpiraEm).toLocaleDateString('pt-BR')}`}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Ações */}
                  {m.statusConta !== 'ATIVA' && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>

                      {/* WhatsApp — Doc 15 Canal 1 */}
                      {link && !expirou && (
                        <button onClick={() => enviarWhatsApp(m)} title="Enviar via WhatsApp" style={{
                          padding: '7px 12px', borderRadius: 8, border: '1px solid #25d36644',
                          background: '#25d36611', color: '#25d366',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600,
                        }}>
                          <MessageCircle size={13} /> WhatsApp
                        </button>
                      )}

                      {/* QR Code — Doc 15 Canal 3 */}
                      {link && !expirou && (
                        <button onClick={() => abrirQR(comLink.length > 0 ? comLink : [m], comLink.findIndex(x => x.id === m.id))} title="Ver QR Code" style={{
                          padding: '7px 12px', borderRadius: 8, border: '1px solid #6c5ce744',
                          background: '#6c5ce711', color: '#6c5ce7',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600,
                        }}>
                          <QrCode size={13} /> QR
                        </button>
                      )}

                      {/* Copiar link */}
                      {link && !expirou && (
                        <button onClick={() => copiar(link, m.id)} title="Copiar link de ativação" style={{
                          padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
                          background: isCopied ? '#10b98122' : 'transparent',
                          color: isCopied ? '#10b981' : 'var(--text-muted)',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600,
                        }}>
                          {isCopied ? <CheckCircle2 size={13} /> : <Link2 size={13} />}
                          {isCopied ? 'Copiado!' : 'Link'}
                        </button>
                      )}

                      {/* Copiar código curto */}
                      {m.codigoDisplay && !expirou && (
                        <button onClick={() => copiar(m.codigoDisplay!, `${m.id}-cod`)} title="Copiar código de 6 dígitos" style={{
                          padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
                          background: copiadoId === `${m.id}-cod` ? '#6c5ce722' : 'transparent',
                          color: copiadoId === `${m.id}-cod` ? '#6c5ce7' : 'var(--text-muted)',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 700,
                          fontFamily: 'monospace', letterSpacing: '0.1em',
                        }}>
                          <Hash size={13} />
                          {m.codigoDisplay}
                        </button>
                      )}

                      {/* Gerar novo link */}
                      <button onClick={() => reenviar(m.id)} disabled={isReenviando} title="Gerar novo link" style={{
                        padding: '7px 12px', borderRadius: 8, border: '1px solid #f59e0b44',
                        background: '#fef3c7', color: '#d97706',
                        cursor: isReenviando ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600,
                        opacity: isReenviando ? 0.6 : 1,
                      }}>
                        <RotateCcw size={13} style={{ animation: isReenviando ? 'spin 1s linear infinite' : 'none' }} />
                        {isReenviando ? 'Gerando...' : 'Novo link'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats rodapé */}
      {!loading && membros.length > 0 && (
        <div style={{ marginTop: '1.5rem', display: 'flex', gap: 16, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          <span>Total: {membros.length}</span>
          <span>✅ Ativas: {membros.filter(m => m.statusConta === 'ATIVA').length}</span>
          <span>⏳ Pendentes: {pendentes.length}</span>
          <span>❌ Expiradas: {membros.filter(m => m.tokenExpiraEm && new Date(m.tokenExpiraEm) < new Date() && m.statusConta !== 'ATIVA').length}</span>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


